import { STORE_BASE_PATH } from "common/utils/config";
import { toISODate } from "common/utils/format/date";
import { isConservativeGenericEstimateEnabled } from "common/utils/delivery-date/generic-estimate";

// 30 seconds
const CACHE_TTL = 30;
const PREFIX = "__JIFFY_PDP_DATE__";

function getCacheProductColorDeliveryEstimate() {
  const storedData = localStorage.getItem(PREFIX);
  let result;

  if (!storedData) return [];

  try {
    const { data, timestamp } = JSON.parse(storedData);

    // Check if data has expired
    if (Date.now() - timestamp > CACHE_TTL * 1000) {
      clearCachedEstimates();
      return [];
    }

    result = data;
  } catch (e) {
    result = [];
  }

  return result;
}

/**
 * Generate cookie name with defined prefix
 * @param productName {string}
 * @param selectedColor {string}
 * @returns "fruitoftheloom-3931_True_Red" {string}
 */
export const buildDataName = (productName, compositionName) => {
  if (!productName || !compositionName) {
    return null; // Return null if either parameter is missing
  }
  const composition = compositionName.replace(/\s+/g, "_");
  const productSlug = productName.replace(/\s+/g, "_");
  return `${productSlug}_${composition}`;
};

/**
 * The function creates a cookie with delivery estimate object, with expiration set by CACHE_TTL
 * @param $productCard {DOMElement}
 */
export const cacheDeliveryEstimate = $productCard => {
  const $getDeliveryDate = $productTile => $productTile.querySelector(".js-plp-delivery-date");
  const estimate = $getDeliveryDate($productCard)?.dataset || {};

  // Do not store cookie if CA store
  if (STORE_BASE_PATH === "/ca") return;
  // not yet rendered
  if (estimate.timeleft === undefined) return;
  // compare if timeleft is 5 minutes or less
  const curTime = Date.now();
  const cutoff = new Date(estimate.timeleft).getTime();
  const diff = Math.floor((cutoff - curTime) / 1000);
  if (diff <= CACHE_TTL) return;

  const { slug, composition } = $productCard.dataset;
  const name = buildDataName(slug, composition);
  if (!name) return; // Don't cache if buildDataName fails
  const deliveryDate = [name, estimate.date, estimate.timeleft];

  cacheProductColorDeliveryEstimate(slug, composition, deliveryDate);
};

export const cacheProductColorDeliveryEstimate = (productSlug, colorName, data) => {
  const dataArray = getCacheProductColorDeliveryEstimate();
  const dataIndex = dataArray.findIndex(element => element[0] === data[0]);

  // Remove the previous delivery estimate data of the given product-composition
  if (dataIndex !== -1) {
    dataArray.splice(dataIndex, 1);
  }

  // Data array should only have a length of 10
  // Before adding an element remove first in element
  if (dataArray.length >= 10) {
    dataArray.shift();
  }

  dataArray.push(data);

  // Store both the data and timestamp
  localStorage.setItem(
    PREFIX,
    JSON.stringify({
      data: dataArray,
      timestamp: Date.now()
    })
  );
};

export const findCacheProductColorDeliveryEstimate = (productSlug, colorName) => {
  const name = buildDataName(productSlug, colorName);
  if (!name) {
    return false; // Return false if buildDataName fails
  }
  const dataArray = getCacheProductColorDeliveryEstimate();
  const data = dataArray.find(element => element[0] === name);

  if (data) {
    // MN-9575: a PLP card writes a marker-less tuple ([name, date, timeleft]) into
    // this shared cache via cacheDeliveryEstimate. Under the conservative flag,
    // trusting it would render a firm "Today/Tomorrow" for a no-zip shopper for the
    // cache TTL. Force a miss so the PDP refetches a marker-carrying ETA. PDP-written
    // tuples always include the marker slot (index 4), so length < 5 identifies the
    // PLP-origin (or pre-deploy stale) case. Flag off keeps full backward compatibility.
    if (data.length < 5 && isConservativeGenericEstimateEnabled()) {
      return false;
    }

    return {
      success: true,
      composition_dates: {
        show_logo: false,
        value_full: data[1],
        timeleft: data[2]
      },
      cart_dates: {
        show_logo: false,
        value_full: data[1],
        timeleft: data[2],
        shipments: [{ date: data[1], full_date: toISODate(data[1]) }]
      },
      mixpanel: typeof data[3] === "object" ? data[3] : undefined,
      // MN-9575: restore the generic marker (older cache tuples lack index 4 and
      // fall back to false, i.e. non-generic).
      generic_estimate: data[4] === true
    };
  }

  return false;
};

export const clearCachedEstimates = () => {
  localStorage.removeItem(PREFIX);
};

export default {};
